import { useState, useEffect, useCallback, useRef } from "react";
import { useRecoilState, useSetRecoilState } from "recoil";
import { useMutation, useQuery, useLazyQuery } from "@apollo/client";
import { useHistory } from "react-router-dom";

import { AllstarModalState } from "MVVM/State/modals";
import {
  StorageStatusAtom,
  SavedClipStatusAtom,
} from "MVVM/State/storagePolicy";
import { ModalType } from "MVVM/@types";
import { analytics } from "MVVM/Analytics";
import {
  CLIP_SAVE_MUTATION,
  CLIP_UNSAVE_MUTATION,
} from "MVVM/GraphQL/mutateSavedClips";
import { CLIP_STORAGE_STATUS } from "MVVM/GraphQL/savedClipStatus";
import { GET_SAVED_CLIPS } from "MVVM/GraphQL/getSavedClips";
import { useUser } from "MVVM/Hooks/useUser";
import {
  saveSelectedClips,
  unsaveSelectedClips,
} from "MVVM/Utilities/SaveClip";

import {
  ESaveClipStep,
  IStorageStatus,
  IClipForDeletion,
  TSavedFilter,
  TSelectionMode,
} from "./@types";

type SortOption = "LATEST" | "VIEWS" | "BEST";

interface HeaderContent {
  title: string;
  subtitle: string;
  clipName?: string;
}

const getHeaderContent = (
  step: ESaveClipStep,
  storageStatus: IStorageStatus,
  clipTitle: string,
  manageMode: boolean,
): HeaderContent => {
  const baseHeaders: Record<string, HeaderContent> = {
    [ESaveClipStep.CHECKING]: {
      title: "Save Clip",
      subtitle: "Checking storage...",
    },
    [ESaveClipStep.SUMMARY]: {
      title: "Save Clip",
      subtitle: "Keep this clip in your storage permanently",
      clipName: clipTitle,
    },
    [ESaveClipStep.SUCCESS]: {
      title: "Clip Saved",
      subtitle: "Your clip has been saved to your storage",
      clipName: clipTitle,
    },
  };

  if (baseHeaders[step]) {
    return baseHeaders[step];
  }

  if (step === ESaveClipStep.NO_STORAGE) {
    return manageMode
      ? {
          title: "Manage Storage",
          subtitle: `${storageStatus.savedCount}/${storageStatus.limit} clips stored`,
        }
      : {
          title: "Storage Full",
          subtitle: `You've reached your limit of ${storageStatus.limit} saved clips`,
        };
  }

  if (step === ESaveClipStep.DELETE_BROWSE) {
    return manageMode
      ? {
          title: "Manage Storage",
          subtitle:
            "Browse all your clips. Save or unsave clips to manage your storage.",
        }
      : {
          title: "Storage Full",
          subtitle:
            "Unsave clips to free up space — they'll convert to drafts and expire",
        };
  }

  return { title: "Save Clip", subtitle: "" };
};

export const useSaveClipFlowViewModel = () => {
  const [allstarModalState, setAllstarModalState] =
    useRecoilState(AllstarModalState);
  const setStorageStatusAtom = useSetRecoilState(StorageStatusAtom);
  const setSavedClipStatus = useSetRecoilState(SavedClipStatusAtom);
  const { allstarUser } = useUser();
  const history = useHistory();
  const hasInitialFetch = useRef(false);

  const [step, setStep] = useState<ESaveClipStep>(ESaveClipStep.CHECKING);
  const [storageStatus, setStorageStatus] = useState<IStorageStatus>({
    savedCount: 0,
    limit: 10,
    hasSpace: true,
  });
  const [selectedClipIds, setSelectedClipIds] = useState<string[]>([]);
  const [currentSort, setCurrentSort] = useState<SortOption>("LATEST");
  const [clipsForDeletion, setClipsForDeletion] = useState<IClipForDeletion[]>(
    [],
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isUnlimited, setIsUnlimited] = useState(false);
  const [savedFilter, setSavedFilter] = useState<TSavedFilter>("all");
  const [selectionMode, setSelectionMode] = useState<TSelectionMode>(null);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const isOpen = allstarModalState.isOpen === ModalType.SaveClipFlow;
  const clipId = allstarModalState.data?.clipId;
  const clipTitle = allstarModalState.data?.clipTitle || "Untitled";
  const clipThumbnail = allstarModalState.data?.clipThumbnail;
  const manageMode = allstarModalState.data?.manageMode === true;

  const updateStorageState = useCallback(
    (savedCount: number, limit: number | null) => {
      const unlimited = limit === null;
      const hasSpace = unlimited || savedCount < limit;
      setStorageStatus({ savedCount, limit: limit ?? 0, hasSpace });
      setStorageStatusAtom({ savedCount, limit, isUnlimited: unlimited });
      return hasSpace;
    },
    [setStorageStatusAtom],
  );

  const { refetch: refetchStorageStatus } = useQuery(CLIP_STORAGE_STATUS, {
    skip: !isOpen,
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (!data?.me?.clipStorageStatus) return;

      const { savedCount, limit } = data.me.clipStorageStatus;

      if (limit === null) {
        setStorageStatusAtom({ savedCount, limit: null, isUnlimited: true });
        setIsUnlimited(true);
        if (manageMode) {
          setStorageStatus({ savedCount, limit: 0, hasSpace: true });
          setStep(ESaveClipStep.DELETE_BROWSE);
        } else {
          setStep(ESaveClipStep.CHECKING);
        }
        return;
      }

      setIsUnlimited(false);
      const hasSpace = savedCount < limit;
      setStorageStatus({ savedCount, limit, hasSpace });

      if (manageMode) {
        setStep(ESaveClipStep.DELETE_BROWSE);
      } else if (!hasSpace) {
        setStep(ESaveClipStep.NO_STORAGE);
      } else {
        setStep(ESaveClipStep.SUMMARY);
      }
    },
    onError: () => setStep(ESaveClipStep.ERROR),
  });

  const [fetchSavedClips, { loading: savedClipsLoading }] = useLazyQuery(
    GET_SAVED_CLIPS,
    {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        const clips = data?.profile?.clips?.nodes || [];
        const pageInfo = data?.profile?.clips?.pageInfo;
        const mapped = clips.map((clip: any) => ({
          id: clip.id,
          internalId: clip.internalId,
          title: clip.title,
          thumbnailUrl: clip.thumbnailUrl,
          views: clip.views,
          score: clip.score ?? 0,
          createdAt: clip.createdAt,
          isSaved: !!clip.saved?.savedAt,
          saved: clip.saved,
        }));

        if (loadingMore) {
          setClipsForDeletion((prev) => [...prev, ...mapped]);
          setLoadingMore(false);
        } else {
          setClipsForDeletion(mapped);
        }

        setEndCursor(pageInfo?.endCursor || null);
        setHasNextPage(pageInfo?.hasNextPage || false);

        if (data?.profile?.savedClipsCount !== undefined) {
          setStorageStatus((prev) => ({
            ...prev,
            savedCount: data.profile.savedClipsCount,
          }));
        }
      },
    },
  );

  const [unsaveClip] = useMutation(CLIP_UNSAVE_MUTATION);

  const [saveClip, { loading: savingClip }] = useMutation(CLIP_SAVE_MUTATION);

  useEffect(() => {
    if (!isOpen) {
      hasInitialFetch.current = false;
      return;
    }

    setSelectedClipIds([]);
    setCurrentSort("LATEST");
    setClipsForDeletion([]);
    setSaveError(null);
    setIsUnlimited(false);
    setSavedFilter("all");
    setSelectionMode(null);
    setEndCursor(null);
    setHasNextPage(false);
    setLoadingMore(false);
    setStep(ESaveClipStep.CHECKING);
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setAllstarModalState({
      data: {},
      functions: {},
      isOpen: ModalType.None,
    });
  }, [setAllstarModalState]);

  const handleSave = useCallback(async () => {
    if (!clipId) return;

    if (
      storageStatus.limit > 0 &&
      storageStatus.savedCount >= storageStatus.limit
    ) {
      setSaveError("Storage is full. Unsave clips to make room.");
      return;
    }

    try {
      const { data } = await saveClip({
        variables: { input: { clipIds: [btoa(`Clip/${clipId}`)] } },
      });

      if (!data?.clipSave?.success) {
        const errorMessage =
          data?.clipSave?.errors?.[0]?.message || "Failed to save clip";
        setSaveError(errorMessage);
        analytics.track("Save Clip Flow - Error", {
          clipId,
          error: errorMessage,
        });
        return;
      }

      const { savedCount, limit } = data.clipSave;
      updateStorageState(savedCount, limit);
      setSaveError(null);
      setSavedClipStatus((prev) => ({ ...prev, [clipId]: true }));
      refetchStorageStatus();

      analytics.track("Save Clip Flow - Success", {
        clipId,
        savedCount,
        limit,
      });

      setAllstarModalState({
        data: {},
        functions: {},
        isOpen: ModalType.None,
      });
    } catch (error: any) {
      setSaveError(error?.message || "Failed to save clip");
      analytics.track("Save Clip Flow - Error", {
        clipId,
        error: error?.message,
      });
    }
  }, [
    clipId,
    saveClip,
    storageStatus,
    updateStorageState,
    setSavedClipStatus,
    setAllstarModalState,
    refetchStorageStatus,
  ]);

  const getSavedParam = (filter: TSavedFilter): boolean | null => {
    if (filter === "saved") return true;
    if (filter === "unsaved") return false;
    return null;
  };

  const fetchClips = useCallback(
    (sort: SortOption, filter?: TSavedFilter) => {
      if (!allstarUser?.user?._id) return;
      setEndCursor(null);
      setHasNextPage(false);
      const saved = getSavedParam(filter ?? savedFilter);
      fetchSavedClips({
        variables: {
          userId: allstarUser.user._id,
          sort,
          statuses: ["READY"],
          saved,
        },
      });
    },
    [allstarUser?.user?._id, fetchSavedClips, savedFilter],
  );

  useEffect(() => {
    if (!isOpen || step !== ESaveClipStep.DELETE_BROWSE) return;
    if (hasInitialFetch.current) return;
    hasInitialFetch.current = true;
    fetchClips(currentSort);
  }, [isOpen, step, fetchClips, currentSort]);

  const handleManageClips = useCallback(() => {
    setStep(ESaveClipStep.DELETE_BROWSE);
    fetchClips(currentSort);
  }, [currentSort, fetchClips]);

  const handleSortChange = useCallback(
    (sort: SortOption) => {
      setCurrentSort(sort);
      setSelectedClipIds([]);
      setSelectionMode(null);
      fetchClips(sort);
    },
    [fetchClips],
  );

  const handleSavedFilterChange = useCallback(
    (filter: TSavedFilter) => {
      setSavedFilter(filter);
      setSelectedClipIds([]);
      setSelectionMode(null);
      fetchClips(currentSort, filter);
    },
    [currentSort, fetchClips],
  );

  const handleLoadMore = useCallback(() => {
    if (!hasNextPage || !endCursor || !allstarUser?.user?._id) return;
    setLoadingMore(true);
    const saved = getSavedParam(savedFilter);
    fetchSavedClips({
      variables: {
        userId: allstarUser.user._id,
        sort: currentSort,
        statuses: ["READY"],
        saved,
        after: endCursor,
      },
    });
  }, [
    hasNextPage,
    endCursor,
    allstarUser?.user?._id,
    savedFilter,
    currentSort,
    fetchSavedClips,
  ]);

  const handleOpenClip = useCallback((id: string) => {
    window.open(`/clip/${id}`, "_blank");
  }, []);

  const remainingSlots = storageStatus.limit - storageStatus.savedCount;

  const handleSelectClip = useCallback(
    (clipIdToSelect: string) => {
      const clip = clipsForDeletion.find((c) => c.id === clipIdToSelect);
      if (!clip) return;

      setSelectedClipIds((prev) => {
        const isDeselecting = prev.includes(clipIdToSelect);

        if (!isDeselecting && !clip.isSaved) {
          const wouldSave = prev.length === 0 || selectionMode === "save";
          if (wouldSave && prev.length >= remainingSlots) return prev;
        }

        const nextIds = isDeselecting
          ? prev.filter((id) => id !== clipIdToSelect)
          : [...prev, clipIdToSelect];

        if (nextIds.length === 0) {
          setSelectionMode(null);
        } else if (!isDeselecting && prev.length === 0) {
          setSelectionMode(clip.isSaved ? "unsave" : "save");
        }

        return nextIds;
      });
    },
    [clipsForDeletion, selectionMode, remainingSlots],
  );

  const isClipSelectable = useCallback(
    (clip: IClipForDeletion) => {
      if (selectionMode === "unsave") return clip.isSaved;

      if (!clip.isSaved) {
        if (selectedClipIds.includes(clip.id)) return true;
        if (
          selectionMode === "save" &&
          selectedClipIds.length >= remainingSlots
        )
          return false;
        if (selectionMode === null && remainingSlots <= 0) return false;
        return true;
      }

      if (selectionMode === null) return true;
      return false;
    },
    [selectionMode, selectedClipIds, remainingSlots],
  );

  const handleBulkAction = useCallback(async () => {
    if (selectedClipIds.length === 0) return;

    setIsDeleting(true);

    try {
      if (selectionMode === "save") {
        const results = await saveSelectedClips(
          selectedClipIds,
          clipsForDeletion,
          saveClip,
        );

        if (!results.success) return;

        analytics.track("Save Clip Flow - Saved Clips", {
          savedCount: results.count,
          source: manageMode ? "manage" : "save_flow",
        });

        updateStorageState(results.savedCount, results.limit);

        setSavedClipStatus((prev) => {
          const updated = { ...prev };
          results.savedIds.forEach((id) => {
            updated[id] = true;
          });
          return updated;
        });

        setClipsForDeletion((prev) =>
          prev.map((clip) =>
            selectedClipIds.includes(clip.id)
              ? { ...clip, isSaved: true }
              : clip,
          ),
        );
      } else {
        const results = await unsaveSelectedClips(
          selectedClipIds,
          clipsForDeletion,
          unsaveClip,
        );

        if (!results.success) return;

        analytics.track("Save Clip Flow - Unsaved Clips", {
          removedCount: results.count,
          source: manageMode ? "manage" : "save_flow",
        });

        const hasSpace = updateStorageState(results.savedCount, results.limit);

        setSavedClipStatus((prev) => {
          const updated = { ...prev };
          results.unsavedIds.forEach((id) => {
            updated[id] = false;
          });
          return updated;
        });

        setClipsForDeletion((prev) =>
          prev.map((clip) =>
            selectedClipIds.includes(clip.id)
              ? { ...clip, isSaved: false }
              : clip,
          ),
        );

        if (!manageMode && hasSpace) {
          setStep(ESaveClipStep.SUMMARY);
        }
      }

      setSelectedClipIds([]);
      setSelectionMode(null);
    } finally {
      setIsDeleting(false);
    }
  }, [
    selectedClipIds,
    selectionMode,
    clipsForDeletion,
    saveClip,
    unsaveClip,
    manageMode,
    updateStorageState,
    setSavedClipStatus,
  ]);

  const handleLetAllstarChoose = useCallback(() => {
    const remaining = storageStatus.limit - storageStatus.savedCount;
    if (remaining <= 0) return;

    const unsavedClips = clipsForDeletion.filter((c) => !c.isSaved);
    if (unsavedClips.length === 0) return;

    const maxViews = Math.max(...unsavedClips.map((c) => c.views), 1);
    const maxScore = Math.max(...unsavedClips.map((c) => c.score), 1);

    const ranked = unsavedClips
      .map((clip) => ({
        ...clip,
        composite:
          (clip.views / maxViews) * 0.5 + (clip.score / maxScore) * 0.5,
      }))
      .sort((a, b) => b.composite - a.composite);

    const toSelect = ranked.slice(0, remaining).map((c) => c.id);
    setSelectedClipIds(toSelect);
    setSelectionMode("save");

    analytics.track("Save Clip Flow - Let Allstar Choose", {
      slotsRemaining: remaining,
      clipsSelected: toSelect.length,
    });
  }, [clipsForDeletion, storageStatus]);

  const handleBackFromDelete = useCallback(() => {
    setSelectedClipIds([]);
    handleClose();
  }, [handleClose]);

  const handleUpgrade = useCallback(() => {
    handleClose();
    history.push("/upgrade");
  }, [handleClose, history]);

  return {
    isOpen,
    step,
    storageStatus,
    clipTitle,
    clipThumbnail,
    manageMode,
    saveError,
    savingClip,
    savedClipsLoading,
    clipsForDeletion,
    selectedClipIds,
    currentSort,
    isDeleting,
    isUnlimited,
    savedFilter,
    selectionMode,
    hasNextPage,
    loadingMore,
    headerContent: getHeaderContent(step, storageStatus, clipTitle, manageMode),
    handleClose,
    handleSave,
    handleManageClips,
    handleSortChange,
    handleSavedFilterChange,
    handleLoadMore,
    handleOpenClip,
    handleSelectClip,
    isClipSelectable,
    handleBulkAction,
    handleLetAllstarChoose,
    handleBackFromDelete,
    handleUpgrade,
  };
};
